import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Sprout, Calendar, Leaf, TrendingUp } from 'lucide-react';
import brazilMap from '@svg-maps/brazil';

const BR = brazilMap.default || brazilMap;
const NE_VIEW = { x: 410, y: 110, w: 190, h: 210 };
const NE_STATES = new Set(['ba', 'pe', 'ce', 'pi', 'ma', 'rn', 'pb', 'al', 'se']);

export const MoringaCityMap = ({ cities = [], labels = {} }) => {
  const [active, setActive] = useState(cities[0] || null);
  const maxPct = Math.max(1, ...cities.map((c) => c.percent || 0));

  const haByState = useMemo(() => {
    const m = {};
    cities.forEach((c) => { if (c.state_id) m[c.state_id] = (m[c.state_id] || 0) + (c.hectares || 0); });
    return m;
  }, [cities]);
  const maxStateHa = Math.max(1, ...Object.values(haByState));

  const svgGround = useMemo(() => {
    const paths = BR.locations.map((loc) => {
      const ha = haByState[loc.id];
      const ne = NE_STATES.has(loc.id);
      const fill = ha
        ? `rgba(76,186,133,${0.3 + 0.6 * (ha / maxStateHa)})`
        : ne ? 'rgba(22,74,50,0.9)' : 'rgba(16,40,29,0.85)';
      return `<path d="${loc.path}" fill="${fill}" stroke="rgba(233,199,103,0.18)" stroke-width="0.6"/>`;
    }).join('');
    return `<svg viewBox="${NE_VIEW.x} ${NE_VIEW.y} ${NE_VIEW.w} ${NE_VIEW.h}" preserveAspectRatio="xMidYMid meet" width="100%" height="100%" style="width:100%;height:100%;overflow:visible">${paths}</svg>`;
  }, [haByState, maxStateHa]);

  return (
    <div className="grid lg:grid-cols-5 gap-8 items-center" data-testid="moringa-city-map">
      <div className="lg:col-span-3">
        <div style={{ perspective: '1200px' }} className="relative select-none py-6">
          <motion.div
            animate={{ rotateZ: [-2.5, 2.5, -2.5] }}
            transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
            style={{ transform: 'rotateX(55deg)', transformStyle: 'preserve-3d', width: '100%', maxWidth: 560, aspectRatio: `${NE_VIEW.w}/${NE_VIEW.h}`, position: 'relative', margin: '0 auto' }}
          >
            <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 55% 45%, rgba(233,199,103,0.10), transparent 60%)', borderRadius: '45%' }} />
            <div className="absolute inset-0" dangerouslySetInnerHTML={{ __html: svgGround }} />
            {cities.map((c, i) => {
              const left = ((c.x - NE_VIEW.x) / NE_VIEW.w) * 100;
              const top = ((c.y - NE_VIEW.y) / NE_VIEW.h) * 100;
              const h = 20 + (c.percent / maxPct) * 110;
              const isA = active && active.name === c.name;
              return (
                <div
                  key={c.name}
                  style={{ position: 'absolute', left: `${left}%`, top: `${top}%`, transformStyle: 'preserve-3d', zIndex: Math.round(top * 10) }}
                  onMouseEnter={() => setActive(c)}
                  data-testid={`city-pin-${c.state_id}-${i}`}
                >
                  {/* ground disc */}
                  <div style={{ position: 'absolute', left: -6, top: -6, width: 12, height: 12, borderRadius: '50%', background: isA ? '#fff' : '#e9c767', boxShadow: `0 0 ${isA ? 16 : 8}px #e9c767` }} />
                  {/* beam (counter-rotated to stand upright) */}
                  <motion.div
                    initial={{ scaleY: 0 }}
                    animate={{ scaleY: 1 }}
                    transition={{ duration: 0.8, delay: i * 0.04 }}
                    style={{
                      position: 'absolute', bottom: 0, left: -1.5, width: 3, height: h,
                      transform: 'rotateX(-55deg)', transformOrigin: 'bottom center',
                      background: 'linear-gradient(to top, rgba(233,199,103,0.05), #e9c767)',
                      borderRadius: 3, boxShadow: isA ? '0 0 16px #e9c767' : '0 0 7px rgba(233,199,103,0.55)',
                    }}
                  />
                  {/* percent chip at top of beam */}
                  <div style={{ position: 'absolute', bottom: h, left: 0, transform: 'translate(-50%,-40%) rotateX(-55deg)', transformOrigin: 'center', whiteSpace: 'nowrap' }}>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${isA ? 'bg-white text-[#07160f]' : 'bg-[#e9c767] text-[#07160f]'}`}>
                      {c.percent}%
                    </span>
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>
        <p className="text-center text-[11px] text-emerald-200/40 mt-2">{labels.hint || 'Hover a city tower to see project details'}</p>
      </div>

      {/* Active city detail */}
      <div className="lg:col-span-2 space-y-4">
        {active && (
          <motion.div key={active.name} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
            className="rounded-2xl border border-[#e9c767]/25 bg-gradient-to-br from-[#0a1f15] to-[#07160f] p-6" data-testid="city-detail">
            <div className="flex items-center gap-2 text-[#e9c767]"><MapPin className="w-4 h-4" /><span className="text-xs uppercase tracking-[0.15em]">{active.state}</span></div>
            <h3 className="mt-1 text-2xl font-bold text-white">{active.name}</h3>
            <p className="text-sm text-emerald-100/60">{active.project}</p>
            <div className="mt-5 grid grid-cols-2 gap-4">
              <div><p className="text-[10px] uppercase tracking-wide text-emerald-200/50">{labels.share || 'Allocation'}</p><p className="text-2xl font-extrabold text-[#e9c767]">{active.percent}%</p></div>
              <div><p className="text-[10px] uppercase tracking-wide text-emerald-200/50">{labels.hectares || 'Hectares'}</p><p className="text-2xl font-extrabold text-white">{(active.hectares || 0).toLocaleString('en-US')}</p></div>
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex items-center justify-between"><span className="flex items-center gap-2 text-emerald-100/60"><Sprout className="w-4 h-4 text-[#e9c767]" />{labels.planting || 'Planting'}</span><span className="text-white">{active.planting}</span></div>
              <div className="flex items-center justify-between"><span className="flex items-center gap-2 text-emerald-100/60"><Calendar className="w-4 h-4 text-[#e9c767]" />{labels.planted || 'Planted'}</span><span className="text-white">{active.planted_date}</span></div>
              <div className="flex items-center justify-between"><span className="flex items-center gap-2 text-emerald-100/60"><Calendar className="w-4 h-4 text-[#e9c767]" />{labels.harvest || 'Next harvest'}</span><span className="text-white">{active.next_harvest}</span></div>
              <div className="flex items-center justify-between"><span className="flex items-center gap-2 text-emerald-100/60"><Leaf className="w-4 h-4 text-[#e9c767]" />{labels.yield || 'Expected yield'}</span><span className="text-white">{(active.yield_tons || 0).toLocaleString('en-US')} t</span></div>
            </div>
          </motion.div>
        )}
        <div className="space-y-1.5 max-h-[220px] overflow-y-auto pr-1">
          {[...cities].sort((a, b) => (b.percent || 0) - (a.percent || 0)).map((c, i) => (
            <button key={c.name} onMouseEnter={() => setActive(c)} onClick={() => setActive(c)}
              className={`w-full flex items-center gap-3 rounded-lg border px-3 py-1.5 text-left transition-colors ${active && active.name === c.name ? 'border-[#e9c767]/50 bg-[#e9c767]/10' : 'border-[#e9c767]/10 bg-[#0a1f15] hover:border-[#e9c767]/30'}`}
              data-testid={`city-row-${i}`}>
              <TrendingUp className="w-3.5 h-3.5 text-[#e9c767] flex-shrink-0" />
              <span className="text-sm text-white flex-1 truncate">{c.name}</span>
              <span className="text-xs text-emerald-200/50">{(c.hectares || 0).toLocaleString('en-US')} ha</span>
              <span className="text-sm font-bold text-[#e9c767] w-12 text-right">{c.percent}%</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MoringaCityMap;
